import React, { useState, useEffect } from 'react'
import _ from 'lodash'
import { useHistory } from 'react-router-dom'
import { Button, Form, Menu, Icon } from 'semantic-ui-react'
import { useForm } from 'react-hook-form'
import { useToolbarDispatch } from 'contexts'
import {
  ConditionDataTable,
  ConditionPanel,
  DataTableType,
  HeaderRow,
  HeaderCell,
  BodyRow,
  BodyCell,
} from 'base/piece/DataTable'
import Textbox from 'component/control/textbox'
import Layout from 'component/layout'
import { User } from 'types'
import { BASE_PATH } from '.'

type ConditionType = {
  name: string
  email: string
}

export const List: React.FC = () => {
  const history = useHistory()
  const toolbarDispatch = useToolbarDispatch()

  const [targetId, setTargetId] = useState<number | null>(null)

  const handleSelectedChange = (selectedRows: User[]) => {
    selectedRows.length === 1
      ? setTargetId(selectedRows[0].id || null)
      : setTargetId(null)
  }

  useEffect(() => {
    toolbarDispatch({
      type: 'SET',
      title: 'ユーザ一覧',
      menuItems: (
        <Menu.Item>
          <Button.Group>
            <Button
              icon='eye'
              content='詳細'
              disabled={!targetId}
              onClick={() => history.push(`${BASE_PATH}/${targetId}/view`)}
            />
            <Button
              icon='plus'
              content='新規'
              onClick={() => history.push(`${BASE_PATH}/new`)}
            />
            <Button
              icon='pencil'
              content='編集'
              disabled={!targetId}
              onClick={() => history.push(`${BASE_PATH}/${targetId}/edit`)}
            />
            <Button
              icon='eraser'
              content='削除'
              disabled={!targetId}
              onClick={() => history.push(`${BASE_PATH}/${targetId}/delete`)}
            />
          </Button.Group>
        </Menu.Item>
      ),
    })
  }, [targetId, history, BASE_PATH])

  return (
    <Layout.Page basic>
      <ConditionDataTable<User, ConditionType>
        dtid='user-datatable'
        url='/crud/user'
        ConditionBlock={ConditionBlock}
        HeaderBlock={HeaderBlock}
        RowBlock={RowBlock}
        sortable
        onSelectedChange={handleSelectedChange}
      />
    </Layout.Page>
  )
}

const ConditionBlock: React.FC<
  DataTableType.ConditionBlockProps<ConditionType>
> = props => {
  const { condition, setCondition, defaultCondition } = props

  const {
    handleSubmit,
    errors,
    control,
    setValue,
    reset,
  } = useForm<ConditionType>()

  useEffect(() => {
    setValue('name', condition?.name || '')
    setValue('email', condition?.email || '')
  }, [condition, setValue])

  const onSubmit = (model: ConditionType) => {
    if (!setCondition) return
    setCondition(state => _.assign({}, state, model))
  }

  const handleReset = () => {
    reset(defaultCondition)
    setCondition && setCondition(defaultCondition)
  }

  return (
    <ConditionPanel
      handleRefresh={handleSubmit(onSubmit)}
      handleReset={handleReset}
      ConditionForm={
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Field error={errors.name ? true : false} width={4}>
            <Textbox.Base.Standard
              label='name'
              control={control}
              errors={errors}
              name='name'
              placeholder='enter user name'
            />
          </Form.Field>
          <Form.Field error={errors.email ? true : false} width={4}>
            <Textbox.Base.Email
              label='email'
              control={control}
              errors={errors}
              name='email'
              placeholder='enter user email address'
            />
          </Form.Field>
        </Form>
      }
    />
  )
}

const HeaderBlock: React.FC<DataTableType.HeaderBlockProps> = props => {
  const { renderRow, ...rest } = props

  return renderRow(
    <>
      <HeaderRow>
        <HeaderCell name='id' defaultWidth={90} {...rest}>
          <span>id</span>
        </HeaderCell>
        <HeaderCell name='name' defaultWidth={90} {...rest}>
          <span>name</span>
        </HeaderCell>
        <HeaderCell name='email' defaultWidth={90} {...rest}>
          <span>email</span>
        </HeaderCell>
        <HeaderCell name='sub' defaultWidth={90} {...rest}>
          <span>sub</span>
        </HeaderCell>
        <HeaderCell name='available' defaultWidth={90} {...rest}>
          <span>available</span>
        </HeaderCell>
        <HeaderCell name='inserted_at' defaultWidth={90} {...rest}>
          <span>inserted_at</span>
        </HeaderCell>
        <HeaderCell name='link' defaultWidth={90} {...rest}>
          <span>link</span>
        </HeaderCell>
      </HeaderRow>
    </>
  )
}

type RowBlockProps = {
  data: User
} & DataTableType.BodyBlockProps

const RowBlock: React.FC<RowBlockProps> = props => {
  const { data, renderRow, selected, handleClick } = props

  const { id, name, email, sub, available, inserted_at: inserted_at } = data

  return renderRow(
    <>
      <BodyRow positive={selected} onClick={handleClick}>
        <BodyCell content={id} />
        <BodyCell content={name} />
        <BodyCell content={email} />
        <BodyCell content={sub} />
        <BodyCell
          content={available ? <Icon name='check' /> : <Icon name='minus' />}
        />
        <BodyCell content={inserted_at} />
        <BodyCell content={''} />
      </BodyRow>
    </>
  )
}

export default List
